import React, { useState, useEffect } from 'react';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Dialog, Select, MenuItem } from '@dsi/react-eds';
import ReactDOM from 'react-dom';
import api from '../../api/axiosInstance';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';

// Type definitions
interface Enm {
  id: string;
  name: string;
  linkedEiapClusters: string[];
}

interface EiapCluster {
  id: string;
  name: string;
  category: string;
}

interface LinkEiapClustersDialogProps {
  open: boolean;
  enmId: string;
  onSave: (enmId: string, linkedClusterIds: string[]) => void;
  onClose: () => void;
}

const LinkEiapClustersDialog: React.FC<LinkEiapClustersDialogProps> = ({ open, enmId, onSave, onClose }) => {
  const { addNotifications } = useSwobNotifications();
  const [clusters, setClusters] = useState<EiapCluster[]>([]);
  const [enm, setEnm] = useState<Enm | null>(null);
  const [selectedClusters, setSelectedClusters] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch EIAP clusters and ENM data when dialog opens
  useEffect(() => {
    if (open && enmId) {
      fetchData();
    }
  }, [open, enmId]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch EIAP clusters and ENM data in parallel
      const [clustersResponse, enmResponse] = await Promise.all([
        api.get('/api/eiap-cluster'),
        api.get(`/api/enm/${enmId}`)
      ]);

      setClusters(clustersResponse.data);
      setEnm(enmResponse.data);
      setSelectedClusters(enmResponse.data.linkedEiapClusters || []);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching data.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleSave = () => {
    onSave(enmId, selectedClusters);
  };

  const handleClose = () => {
    setSelectedClusters(enm?.linkedEiapClusters || []);
    onClose();
  };

  return ReactDOM.createPortal(
    <ReactEDSWrapper
      component={Dialog}
      className={'overFlowDialog'}
      show={open}
      onClose={handleClose}
      title="Link EIAP Clusters"
      subtitle={`Configure EIAP cluster links for ${enm?.name || 'ENM'}`}
      bottom={
        <ReactEDSWrapper
          component={Button}
          primary
          onClick={handleSave}
        >
          Save
        </ReactEDSWrapper>
      }
    >
      {open && (
        <div style={{ padding: '24px' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>Loading data...</p>
            </div>
          ) : (
            <>
              <p style={{ marginBottom: '16px', color: '#6a6a6a' }}>
                Select EIAP cluster instances to link with this ENM. Linked clusters will be associated with this ENM instance.
              </p>

              <ReactEDSWrapper
                component={Select}
                type="input-single"
                label="Select EIAP Clusters"
                value={selectedClusters}
                onChange={(event: any) => {
                  const newValue = event?.target?.value ?? event;
                  setSelectedClusters(Array.isArray(newValue) ? newValue : []);
                }}
              >
                {clusters.map((cluster) => (
                  <ReactEDSWrapper key={cluster.id} component={MenuItem} value={cluster.id}>
                    {`${cluster.name} (${cluster.category})`}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </>
          )}
        </div>
      )}
    </ReactEDSWrapper>,
    document.body
  );
};

export default LinkEiapClustersDialog;
